import React from "react";
import Lexical, {$getRoot} from 'lexical';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getNearestBlockElementAncestorOrThrow, mergeRegister} from "@lexical/utils";
import {registerPlainText} from "@lexical/plain-text";
import {LexicalEditorToolbar} from "./LexicalEditorToolbar";
import {debug_log} from "../../../utils/debug";
import {$getLexicalContent, $insertDataTransferForRichText, $getHtmlContent} from "@lexical/clipboard";
import {ColorStyleType, FontStyleType, SizeStyleType} from "../../../models/CellContent/TextStyleType";

type LexicalEditorProps = {
    editorState?: Lexical.EditorState;
    isEdit: boolean;
    showToolbar: boolean;
    onChange: (editor_state: Lexical.EditorState) => void;
    onBlur: (editor_state: Lexical.EditorState) => void;
    grabEditor?: (editor: Lexical.LexicalEditor) => void;
}

export const LexicalEditor = (props: LexicalEditorProps) => {

    const initialConfig = {
        namespace: "CellEditor",
        theme: {},
        onError: (error: Error, editor: Lexical.LexicalEditor) => {
            throw error;
        },
        nodes: [HtmlConversionNode],
    };

    return (
        <>
            <LexicalComposer initialConfig={initialConfig}>
                <LexicalEditorToolbar isShow={props.showToolbar}/>
                <JcTextPlugin
                    contentEditable={<ContentEditable style={{outline: 'none'}}/>}
                />
                <OnChangePlugin onChange={props.onChange} ignoreSelectionChange={true}/>
                <OnBlurPlugin onBlur={props.onBlur}/>
                <EditorStatePlugin editorState={props.editorState}/>
                <IsEditPlugin isEdit={props.isEdit}/>
                <GrabEditorPlugin grabEditor={props.grabEditor}/>

                <HistoryPlugin/>
            </LexicalComposer>
        </>
    );
}

export class HtmlConversionNode extends Lexical.TextNode {

    static getType(): string {
        return "dummy";
    }

    static clone(node: HtmlConversionNode): HtmlConversionNode {
        return new HtmlConversionNode('');
    }


    static importJSON(serialized_node: Lexical.SerializedTextNode): Lexical.TextNode {
        return super.importJSON(serialized_node);
    }

    exportJSON(): Lexical.SerializedTextNode {
        return super.exportJSON();
    }

    static importDOM(): Lexical.DOMConversionMap | null {
        return {
            span: (node: Node) => ({
                conversion: HtmlConversionNode.convertSpanElement,
                priority: 4,
            }),
            strong: (node: Node) => ({
                conversion: HtmlConversionNode.convertSpanElement,
                priority: 4,
            }),
        }
    }

    static convertSpanElement(dom_node: Node): Lexical.DOMConversionOutput {
        if (!(dom_node instanceof HTMLElement)) {
            throw new Error("unexpected dom");
        }

        let style = '';
        const font = FontStyleType.fromCssValueOrDefault(dom_node.style.fontFamily, undefined);
        if (font) {
            style += font.toCssString();
        }
        const size = SizeStyleType.fromCssValueOrDefault(dom_node.style.fontSize, undefined);
        if (size) {
            style += size.toCssString();
        }
        const color = ColorStyleType.fromCssValueOrDefault(dom_node.style.color, undefined);
        if (color) {
            style += color.toCssString();
        }

        // Google Docs uses span tags + font-weight for bold text
        const is_bold = (dom_node.nodeName.toLowerCase() === 'strong' || dom_node.style.fontWeight === '700');

        return {
            forChild: (lexicalNode) => {
                if (!Lexical.$isTextNode(lexicalNode)) {
                    return lexicalNode;
                }

                if (is_bold) {
                    lexicalNode.toggleFormat('bold');
                }
                if (0 < style.length) {
                    lexicalNode.setStyle(style);
                }

                return lexicalNode;
            },
            node: null,
        };
    }
}

function GrabEditorPlugin({grabEditor}: { grabEditor?: (editor: Lexical.LexicalEditor) => void }) {
    const [editor] = useLexicalComposerContext();
    if (grabEditor !== undefined) {
        grabEditor(editor);
    }

    return null;
}

function EditorStatePlugin({editorState}: { editorState?: Lexical.EditorState }) {
    const [editor] = useLexicalComposerContext();
    React.useEffect(() => {
        //console.log(editor, editorState, editor.getEditorState() === editorState);

        if (editorState && !editorState.isEmpty() && editor.getEditorState() !== editorState) {
            debug_log("EditorStatePlugin:setEditorState", editorState);
            editor.setEditorState(editorState);
        }
    }, [editor, editorState]);

    return null;
}

function IsEditPlugin({isEdit}: { isEdit: boolean }) {
    const [editor] = useLexicalComposerContext();

    React.useLayoutEffect(() => {
        // editor.setEditable によって呼ばれるリスナを登録
        editor.registerEditableListener((editable: boolean) => {
            if (editable) {
                // ここで何かしらの変更が無いとフォーカスされないことに注意
                editor.update(() => {
                    const root = $getRoot();
                    if (root.getChildrenSize()) {
                        root.selectEnd();
                    }
                });
            }
        });

    }, [editor]);


    React.useEffect(() => {
        if (isEdit) {
            // editor.focus() と editor.setEditable(true) をそれぞれ呼び出すだけだと、何故かフォーカスしてくれない
            // editor.focus の callback で setEditable(true) し、
            // setEditable によって呼び出される registerEditableListener との組み合わせにより上手くフォーカスししてくれたので、
            // 詳しい理由は不明だけどこの呼び方で対応している
            editor.focus(() => {
                editor.setEditable(true);
            });

        } else {
            editor.setEditable(false);
        }
    }, [editor, isEdit]);

    return null;
}

function OnBlurPlugin({onBlur}: { onBlur: (editor_state: Lexical.EditorState) => void }): null {

    const [editor] = useLexicalComposerContext();
    React.useLayoutEffect(() => {
        return editor.registerCommand(
            Lexical.BLUR_COMMAND,
            (payload) => {
                onBlur(editor.getEditorState());
                return true;
            },
            Lexical.COMMAND_PRIORITY_EDITOR,
        );
    }, [editor, onBlur]);

    return null;

}


function JcTextPlugin(props: {
    contentEditable: JSX.Element | null,
}): JSX.Element {
    const [editor] = useLexicalComposerContext();
    useJcTextSetup(editor);

    return (
        <>
            {props.contentEditable}
        </>
    );
}

function useJcTextSetup(
    editor: Lexical.LexicalEditor,
): void {
    React.useLayoutEffect(() => {
        return mergeRegister(
            registerPlainText(editor),
            registerJcSupport(editor),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);
}

function registerJcSupport(
    editor: Lexical.LexicalEditor,
): () => void {

    return mergeRegister(
        editor.registerCommand(
            Lexical.KEY_ENTER_COMMAND,
            (event: KeyboardEvent | null) => {
                if (!Lexical.$isRangeSelection(Lexical.$getSelection())) {
                    return false;
                }
                if (event !== null) {
                    event.preventDefault();
                    if (event.altKey || event.ctrlKey || event.metaKey) {
                        return editor.dispatchCommand(Lexical.INSERT_PARAGRAPH_COMMAND, undefined);
                    }
                }

                editor.blur();
                return true; // stop propagation
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
            Lexical.KEY_ESCAPE_COMMAND,
            (payload) => {
                const selection = Lexical.$getSelection();
                if (!Lexical.$isRangeSelection(selection)) {
                    return false;
                }
                editor.blur();
                return true;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),

        editor.registerCommand(
            Lexical.INSERT_PARAGRAPH_COMMAND,
            (payload) => {
                const selection = Lexical.$getSelection();
                if (!Lexical.$isRangeSelection(selection)) {
                    return false;
                }
                selection.insertParagraph();
                return true;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),


        editor.registerCommand(
            Lexical.KEY_MODIFIER_COMMAND,
            (event: KeyboardEvent | null) => {
                if (event === null) {
                    return false;
                }

                if (event.ctrlKey) {
                    if (event.key === 'i') {
                        event.preventDefault();
                        debug_log("EditorState", editor.getEditorState().toJSON());
                        return true;
                    } else if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        editor.dispatchCommand(Lexical.FORMAT_ELEMENT_COMMAND, 'right');
                        return true;
                    } else if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        editor.dispatchCommand(Lexical.FORMAT_ELEMENT_COMMAND, 'left');
                        return true;
                    }

                }
                return false;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),

        editor.registerCommand(
            Lexical.FORMAT_TEXT_COMMAND,
            (payload: any) => {
                const selection = Lexical.$getSelection();
                if (!Lexical.$isRangeSelection(selection)) {
                    return false;
                }

                // ひとまず bold のみをサポート
                if (payload === 'bold') {
                    const format: Lexical.TextFormatType = payload;
                    selection.formatText(format);
                }
                return true;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),

        editor.registerCommand(
            Lexical.FORMAT_ELEMENT_COMMAND,
            (format: Lexical.ElementFormatType) => {
                const selection = Lexical.$getSelection();
                if (!Lexical.$isRangeSelection(selection) && !Lexical.$isNodeSelection(selection)) {
                    return false;
                }
                selection.getNodes().forEach(node => {
                    $getNearestBlockElementAncestorOrThrow(node).setFormat(format);
                });
                return true;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),

        editor.registerCommand(
            Lexical.COPY_COMMAND,
            (event: ClipboardEvent) => {
                const selection = Lexical.$getSelection();
                if (Lexical.$isRangeSelection(selection)) {
                    onCopyForRichText(event, editor);
                    event.stopPropagation();
                    return true;
                }
                return false;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
            Lexical.CUT_COMMAND,
            (event: ClipboardEvent) => {
                const selection = Lexical.$getSelection();
                if (Lexical.$isRangeSelection(selection)) {
                    onCutForRichText(event, editor);
                    event.stopPropagation();
                    return true;
                }
                return false;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
            Lexical.PASTE_COMMAND,
            (event: ClipboardEvent) => {
                const selection = Lexical.$getSelection();
                if (Lexical.$isRangeSelection(selection)) {
                    onPasteForRichText(event, editor);
                    event.stopPropagation();
                    return true;
                }
                return false;
            },
            Lexical.COMMAND_PRIORITY_NORMAL,
        ),
    );
}


function onPasteForRichText(event: ClipboardEvent, editor: Lexical.LexicalEditor): void {
    event.preventDefault();
    editor.update(() => {
        const selection = Lexical.$getSelection();
        const clipboardData = event.clipboardData;
        if (
            clipboardData != null && Lexical.$isRangeSelection(selection)
        ) {
            $insertDataTransferForRichText(clipboardData, selection, editor);
        }
    });
}

function onCopyForRichText(event: ClipboardEvent, editor: Lexical.LexicalEditor): void {
    event.preventDefault();
    editor.update(() => {
        const clipboardData = event.clipboardData;
        const selection = Lexical.$getSelection();
        if (selection !== null) {
            if (clipboardData != null) {
                const htmlString = $getHtmlContent(editor);
                const lexicalString = $getLexicalContent(editor);
                if (htmlString !== null) {
                    clipboardData.setData('text/html', htmlString);
                }
                if (lexicalString !== null) {
                    clipboardData.setData('application/x-lexical-editor', lexicalString);
                }
                clipboardData.setData('text/plain', selection.getTextContent());
            }
        }
    });
}

function onCutForRichText(event: ClipboardEvent, editor: Lexical.LexicalEditor): void {
    onCopyForRichText(event, editor);
    editor.update(() => {
        const selection = Lexical.$getSelection();
        if (Lexical.$isRangeSelection(selection)) {
            selection.removeText();
        }
    });
}
